export interface FormDraft {
  id: string
  formType:
    | 'protocol'
    | 'furniture'
    | 'animals'
    | 'keys'
    | 'meters'
    | 'rooms'
    | 'control-questions'
    | 'household-appliances'
  protocolId: number
  data: any
  timestamp: number
  autoSaved: boolean
}

export class FormPersistence {
  private prefix = 'resi4rent-form-'

  /**
   * Save form data to localStorage
   */
  save(key: string, data: any, formType: FormDraft['formType'], protocolId: number): void {
    try {
      const draft: FormDraft = {
        id: key,
        formType,
        protocolId,
        data,
        timestamp: Date.now(),
        autoSaved: true
      }

      const storageKey = this.prefix + key
      localStorage.setItem(storageKey, JSON.stringify(draft))

      console.log(`Form draft saved: ${key} (${formType})`)
    } catch (error) {
      console.error('Failed to save form draft:', error)
    }
  }

  /**
   * Load form data from localStorage
   */
  load(key: string): any {
    try {
      const storageKey = this.prefix + key
      const stored = localStorage.getItem(storageKey)

      if (!stored) {
        return null
      }

      const draft: FormDraft = JSON.parse(stored)

      // Check if draft is not too old (7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000 // 7 days
      if (Date.now() - draft.timestamp > maxAge) {
        this.clear(key)
        return null
      }

      console.log(`Form draft loaded: ${key} (${draft.formType})`)
      return draft.data
    } catch (error) {
      console.error('Failed to load form draft:', error)
      this.clear(key) // Clear corrupted draft
      return null
    }
  }

  /**
   * Clear specific form draft
   */
  clear(key: string): void {
    try {
      const storageKey = this.prefix + key
      localStorage.removeItem(storageKey)
      console.log(`Form draft cleared: ${key}`)
    } catch (error) {
      console.error('Failed to clear form draft:', error)
    }
  }

  /**
   * List all draft keys
   */
  listDrafts(): string[] {
    try {
      const drafts: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          const draftKey = key.replace(this.prefix, '')
          drafts.push(draftKey)
        }
      }

      return drafts
    } catch (error) {
      console.error('Failed to list form drafts:', error)
      return []
    }
  }

  /**
   * Get all draft objects with metadata
   */
  getAllDrafts(): FormDraft[] {
    try {
      const drafts: FormDraft[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          try {
            const stored = localStorage.getItem(key)
            if (stored) {
              const draft: FormDraft = JSON.parse(stored)

              // Check if draft is not too old
              const maxAge = 7 * 24 * 60 * 60 * 1000 // 7 days
              if (Date.now() - draft.timestamp <= maxAge) {
                drafts.push(draft)
              } else {
                // Remove old draft
                localStorage.removeItem(key)
              }
            }
          } catch (error) {
            console.error(`Failed to parse draft ${key}:`, error)
            localStorage.removeItem(key) // Remove corrupted draft
          }
        }
      }

      // Sort by timestamp (newest first)
      return drafts.sort((a, b) => b.timestamp - a.timestamp)
    } catch (error) {
      console.error('Failed to get all drafts:', error)
      return []
    }
  }

  /**
   * Get drafts for specific protocol
   */
  getProtocolDrafts(protocolId: number): FormDraft[] {
    return this.getAllDrafts().filter((draft) => draft.protocolId === protocolId)
  }

  /**
   * Clear all drafts (cleanup)
   */
  clearAll(): void {
    try {
      const keysToRemove: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key)
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key)
      })

      console.log(`Cleared ${keysToRemove.length} form drafts`)
    } catch (error) {
      console.error('Failed to clear all form drafts:', error)
    }
  }

  /**
   * Clear old drafts (older than 7 days)
   */
  clearOldDrafts(): void {
    try {
      const maxAge = 7 * 24 * 60 * 60 * 1000 // 7 days
      let clearedCount = 0

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          try {
            const stored = localStorage.getItem(key)
            if (stored) {
              const draft: FormDraft = JSON.parse(stored)
              if (Date.now() - draft.timestamp > maxAge) {
                localStorage.removeItem(key)
                clearedCount++
              }
            }
          } catch (error) {
            // Remove corrupted drafts
            localStorage.removeItem(key)
            clearedCount++
          }
        }
      }

      if (clearedCount > 0) {
        console.log(`Cleared ${clearedCount} old form drafts`)
      }
    } catch (error) {
      console.error('Failed to clear old form drafts:', error)
    }
  }

  /**
   * Generate unique form key
   */
  static generateFormKey(
    formType: FormDraft['formType'],
    protocolId: number,
    additionalId?: string | number
  ): string {
    const base = `${formType}-${protocolId}`
    return additionalId ? `${base}-${additionalId}` : base
  }
}

// Export singleton instance
export const formPersistence = new FormPersistence()
